class KlimossReturns extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.panels = [...this.querySelectorAll('[data-step-panel]')];
    this.progressItems = [...this.querySelectorAll('[data-progress-step]')];
    this.step = 1;
    this.storageKey = 'klimossReturnRequest';

    if (this.querySelector('[data-success-reference]')) {
      this.setupSuccess();
      return;
    }

    if (!this.form) return;
    this.classList.add('is-enhanced');
    this.form.querySelectorAll('[data-next]').forEach((button) => button.addEventListener('click', () => this.next()));
    this.form.querySelectorAll('[data-back]').forEach((button) => button.addEventListener('click', () => this.goTo(this.step - 1)));
    this.form.querySelectorAll('input[name="contact[Request type]"]').forEach((input) => input.addEventListener('change', () => this.updateConditionals()));
    this.form.addEventListener('submit', (event) => this.prepareSubmission(event));
    const restored = this.restoreFormState();
    this.updateConditionals();
    this.goTo(restored ? 3 : 1);
  }

  fieldsForStep(step) {
    return [...this.querySelectorAll(`[data-step-panel="${step}"] input, [data-step-panel="${step}"] select, [data-step-panel="${step}"] textarea`)]
      .filter((field) => !field.disabled && field.type !== 'hidden');
  }

  validateStep(step) {
    let valid = true;
    this.fieldsForStep(step).forEach((field) => {
      const fieldValid = field.checkValidity();
      field.setAttribute('aria-invalid', fieldValid ? 'false' : 'true');
      if (!fieldValid) valid = false;
    });
    if (!valid) {
      const firstInvalid = this.fieldsForStep(step).find((field) => !field.checkValidity());
      firstInvalid?.focus();
      firstInvalid?.reportValidity();
    }
    return valid;
  }

  next() {
    if (this.validateStep(this.step)) this.goTo(Math.min(3, this.step + 1));
  }

  goTo(step) {
    this.step = step;
    this.panels.forEach((panel) => { panel.hidden = Number(panel.dataset.stepPanel) !== step; });
    this.progressItems.forEach((item) => {
      const itemStep = Number(item.dataset.progressStep);
      item.classList.toggle('is-active', itemStep === step);
      item.classList.toggle('is-complete', itemStep < step);
      if (itemStep === step) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    });
    this.querySelector('.kl-returns__progress')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateConditionals() {
    const type = this.form.querySelector('input[name="contact[Request type]"]:checked')?.value || '';
    const exchange = type === 'החלפת מידה' || type === 'החלפת פריט';
    const evidence = ['פריט פגום', 'פריט שגוי', 'פריט חסר'].includes(type);
    this.toggleGroup('[data-exchange-fields]', exchange);
    this.toggleGroup('[data-condition-fields]', !evidence && type !== '');
    this.toggleGroup('[data-evidence-note]', evidence);
  }

  toggleGroup(selector, show) {
    const group = this.querySelector(selector);
    if (!group) return;
    group.hidden = !show;
    group.querySelectorAll('[data-required-when-visible]').forEach((field) => {
      field.required = show;
      field.disabled = !show;
    });
  }

  createReference() {
    const stamp = Date.now().toString(36).slice(-6).toUpperCase();
    const random = Math.random().toString(36).slice(2, 4).toUpperCase();
    return `KLM-${stamp}${random}`;
  }

  restoreFormState() {
    let data = {};
    try { data = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}'); } catch (_) {}
    if (!data.pending || !data.submittedAt || Date.now() - data.submittedAt > 30 * 60 * 1000) return false;

    Object.entries(data).forEach(([name, value]) => {
      if (!name.startsWith('contact[')) return;
      this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach((field) => {
        if (field.type === 'radio' || field.type === 'checkbox') field.checked = field.value === value;
        else field.value = value;
      });
    });

    this.querySelector('[data-server-error]')?.removeAttribute('hidden');
    return true;
  }

  prepareSubmission(event) {
    if (!this.validateStep(3)) {
      event.preventDefault();
      return;
    }

    const reference = this.createReference();
    this.form.querySelector('[data-reference-input]').value = reference;
    const data = Object.fromEntries(new FormData(this.form).entries());
    data.reference = reference;
    data.pending = true;
    data.submittedAt = Date.now();
    try { sessionStorage.setItem(this.storageKey, JSON.stringify(data)); } catch (_) {}
    const submit = this.form.querySelector('[type="submit"]');
    if (submit) {
      submit.disabled = true;
      submit.textContent = 'פותחת בקשה…';
    }
  }

  setupSuccess() {
    let data = {};
    try { data = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}'); } catch (_) {}
    const reference = data.reference || this.createReference();
    const referenceNode = this.querySelector('[data-success-reference]');
    if (referenceNode) referenceNode.textContent = reference;

    const get = (name) => data[`contact[${name}]`] || '';
    const lines = [
      'היי צוות KLIMOSS, פתחתי בקשת החזרה/החלפה.',
      `מספר בקשה: ${reference}`,
      `מספר הזמנה: ${get('Order number')}`,
      `שם: ${get('name')}`,
      `סוג הבקשה: ${get('Request type')}`,
      `פריט: ${get('Item')}`,
      get('Current size') ? `מידה שהתקבלה: ${get('Current size')}` : '',
      get('Requested size') ? `מידה מבוקשת: ${get('Requested size')}` : '',
      `סיבה: ${get('Reason')}`,
      get('body') ? `פרטי הבקשה: ${get('body')}` : '',
      ['פריט פגום', 'פריט שגוי', 'פריט חסר'].includes(get('Request type')) ? 'אצרף תמונות בשיחה.' : '',
      'אשמח לקבל הנחיות להמשך. תודה.'
    ].filter(Boolean);

    const phone = this.dataset.whatsapp || '972548956569';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(lines.join('\n'))}`;
    const link = this.querySelector('[data-whatsapp-link]');
    if (link) link.href = url;

    let remaining = 4;
    const countdown = this.querySelector('[data-countdown]');
    const timer = window.setInterval(() => {
      remaining -= 1;
      if (countdown) countdown.textContent = remaining > 0 ? `WhatsApp ייפתח בעוד ${remaining} שניות…` : 'פותחת WhatsApp…';
      if (remaining <= 0) {
        window.clearInterval(timer);
        window.location.assign(url);
      }
    }, 1000);
    link?.addEventListener('click', () => window.clearInterval(timer), { once: true });
    try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
  }
}

if (!customElements.get('klimoss-returns')) customElements.define('klimoss-returns', KlimossReturns);
